// Mobile-first layout for Bonapisca
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../stores/authStore';
import { useColors } from '../../contexts/ColorContext';
import { Material3BottomNavigation } from './Material3Navigation';
import AppFooter from './AppFooter';
import { LanguageSwitcher } from '../common/LanguageSwitcher';
import { Material3IconButton, Material3Button } from '../ui/Material3Button';
import { Material3Card } from '../ui/Material3Card';
import { PUBLIC_ACCOUNT_FEATURES_ENABLED } from '../../config/publicFeatures';
import { withCurrentLocalePath } from '../../utils/localePath';

interface MobileHeaderProps {
  title?: string;
  showBack?: boolean;
  onBack?: () => void;
  rightAction?: React.ReactNode;
}

const primaryNavItems = [
  { labelKey: 'navigation.home', path: '/' },
  { labelKey: 'navigation.now', path: '/whats-on-now' },
  { labelKey: 'navigation.guide', path: '/codex/' },
  { labelKey: 'navigation.conditions', path: '/toolbox' }
] as const;

const stripLocalePrefix = (pathname: string) => pathname.replace(/^\/(en|it|es|fr|de)(?=\/|$)/, '') || '/';

const MobileHeader: React.FC<MobileHeaderProps> = ({
  title,
  showBack = false,
  onBack,
  rightAction,
}) => {
  const { t } = useTranslation(['common']);
  const navigate = useNavigate();
  const location = useLocation();
  const { dynamicColors } = useColors();

  const isActiveDesktopPath = (path: string) => {
    const activePath = stripLocalePrefix(location.pathname);
    if (path === '/') {
      return activePath === '/';
    }

    return activePath === path || activePath.startsWith(`${path}/`);
  };

  return (
    <header
      className="border-b safe-padding sticky top-0 z-50 bg-white transition-colors duration-150"
      style={{
        backgroundColor: dynamicColors.surface,
        borderColor: dynamicColors.outline,
      }}
    >
      <div className="flex h-14 items-center justify-between px-3 lg:hidden">
        {showBack ? (
          <Material3IconButton
            icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>}
            onClick={onBack}
            aria-label={t('actions.back')}
            size="lg"
          />
        ) : (
          <button
            type="button"
            onClick={() => navigate(withCurrentLocalePath('/', location.pathname))}
            className="flex h-9 w-9 items-center justify-center rounded-2xl shadow-sm transition-colors duration-150"
            style={{ backgroundColor: dynamicColors.primary }}
            aria-label={t('navigation.home')}
          >
            <div className="text-lg">🌊</div>
          </button>
        )}

        <h1 className="flex-1 truncate px-2 text-center text-sm font-semibold tracking-tight text-on-surface sm:px-3 sm:text-base">
          {title || t('app.title')}
        </h1>

        <div className="flex items-center space-x-2">
          {rightAction || <UserMenu />}
        </div>
      </div>

      <div className="hidden h-20 items-center justify-between px-8 lg:flex">
        <button
          type="button"
          onClick={() => navigate(withCurrentLocalePath('/', location.pathname))}
          className="flex items-center gap-3 rounded-full px-3 py-2 text-left transition-colors duration-150 hover:bg-surface-200"
          aria-label={t('navigation.home')}
        >
          <div
            className="flex h-11 w-11 items-center justify-center rounded-2xl shadow-elevation-2"
            style={{ backgroundColor: dynamicColors.primary }}
          >
            <div className="text-lg">🌊</div>
          </div>
          <div>
            <div className="font-display text-xl font-semibold leading-none text-on-surface">{t('app.title')}</div>
            <div className="text-xs font-medium uppercase tracking-[0.16em] text-on-surface-variant">{t('app.taglineShort')}</div>
          </div>
        </button>

        {!showBack && (
          <nav className="flex items-center gap-2 rounded-full border border-outline/70 bg-white px-3 py-2 shadow-sm">
            {primaryNavItems.map((item) => {
              const isActive = isActiveDesktopPath(item.path);
              return (
                <button
                  key={item.path}
                  type="button"
                  onClick={() => navigate(withCurrentLocalePath(item.path, location.pathname))}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-200 ${isActive ? 'bg-primary-500 text-white' : 'text-on-surface-variant hover:bg-primary-500/10 hover:text-primary-600'}`}
                >
                  {t(item.labelKey)}
                </button>
              );
            })}
          </nav>
        )}

        <div className="flex items-center gap-3">
          <div className="flex items-center space-x-2">
            {rightAction || <UserMenu />}
          </div>
        </div>
      </div>
    </header>
  );
};

const UserMenu: React.FC = () => {
  const { t } = useTranslation(['common', 'auth']);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAuthenticated } = useAuth();
  const { dynamicColors } = useColors();

  if (!PUBLIC_ACCOUNT_FEATURES_ENABLED || !isAuthenticated || !user) {
    return (
      <LanguageSwitcher 
        variant="compact" 
        showFlags={true} 
        showNativeNames={false} 
      />
    );
  }

  const handleLogout = async () => {
    try {
      await logout();
      setIsOpen(false);
      window.location.replace('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          setIsOpen((open) => !open);
        }}
        aria-label={t('layout.userMenu')}
        className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full border-none bg-transparent p-3 text-primary-500 transition-colors duration-200 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40 bg-black/20" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Menu */}
          <div
            className="absolute top-full right-0 mt-3 w-72 z-50"
            onClick={(event) => event.stopPropagation()}
          >
            <Material3Card
              variant="elevated"
              className="w-full !border-none shadow-xl"
            >
            {/* User info */}
            <div 
              className="px-6 py-4 border-b transition-colors duration-200"
              style={{ borderColor: `${dynamicColors.outline}80` }} // 50% opacity
            >
              <div className="flex items-center space-x-3">
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center transition-colors duration-200"
                  style={{
                    backgroundColor: dynamicColors.primary,
                  }}
                >
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div>
                  <div 
                    className="text-sm font-semibold transition-colors duration-200"
                    style={{ color: dynamicColors.onSurface }}
                  >
                    {user.signInDetails?.loginId || t('layout.bonaPiscaAngler')}
                  </div>
                  <div 
                    className="text-xs transition-colors duration-200"
                    style={{ color: dynamicColors.onSurfaceVariant }}
                  >
                    {t('layout.experiencedAngler')} {/* TODO: Get from user attributes */}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Menu items */}
            <div className="py-2">
              <Material3Button
                variant="text"
                size="md"
                onClick={() => { setIsOpen(false); navigate(withCurrentLocalePath('/whats-on-now', location.pathname)); }}
                fullWidth
                className="!justify-start !px-6 !py-3 text-left text-sm text-neutral-700 hover:bg-neutral-50 font-medium"
              >
                {t('navigation.now')}
              </Material3Button>

              <Material3Button
                variant="text"
                size="md"
                onClick={() => { setIsOpen(false); navigate(withCurrentLocalePath('/toolbox', location.pathname)); }}
                fullWidth
                className="!justify-start !px-6 !py-3 text-left text-sm text-neutral-700 hover:bg-neutral-50 font-medium"
              >
                {t('navigation.conditions')}
              </Material3Button>

              <div className="border-t border-neutral-200/50 my-2 mx-6"></div>
              
              <button
                type="button"
                onClick={handleLogout}
                className="flex min-h-[44px] w-full items-center gap-3 px-6 py-3 text-left text-sm font-medium text-mediterranean-coral-600 transition-colors duration-200 hover:bg-mediterranean-coral-50"
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-mediterranean-coral-100 transition-transform group-hover:scale-110">
                  <svg className="w-4 h-4 text-mediterranean-coral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </div>
                <span>{t('auth:sign_out')}</span>
              </button>
            </div>
            
            {/* Language switcher at bottom */}
            <div className="border-t border-neutral-200/50 pt-4 pb-2 px-6">
              <div className="text-xs font-medium text-neutral-500 mb-3">{t('auth:preferred_language')}</div>
              <LanguageSwitcher 
                variant="compact" 
                showFlags={true} 
                showNativeNames={false}
                className="w-full"
              />
            </div>
            </Material3Card>
          </div>
        </>
      )}
    </div>
  );
};

interface OfflineIndicatorProps {
  isOnline: boolean;
}

const OfflineIndicator: React.FC<OfflineIndicatorProps> = ({ isOnline }) => {
  const { t } = useTranslation(['common']);
  const { dynamicColors } = useColors();
  
  if (isOnline) return null;

  return (
    <div 
      className="text-white px-4 py-3 text-sm text-center shadow-lg animate-slide-down transition-colors duration-200"
      style={{
        backgroundColor: dynamicColors.error,
      }}
    >
      <div className="flex items-center justify-center gap-2">
        <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364" />
          </svg>
        </div>
        <span className="font-medium">{t('status.offline', { defaultValue: 'Offline Mode' })}</span>
        <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
      </div>
    </div>
  );
};

interface MobileLayoutProps {
  children?: React.ReactNode;
  title?: string;
  showBack?: boolean;
  onBack?: () => void;
  rightAction?: React.ReactNode;
  showBottomNav?: boolean;
}

export const MobileLayout: React.FC<MobileLayoutProps> = ({
  children,
  title,
  showBack,
  onBack,
  rightAction,
  showBottomNav = true,
}) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showMobileNav, setShowMobileNav] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < 1024 : true));
  const { dynamicColors } = useColors();

  React.useEffect(() => {
    const syncViewport = () => setShowMobileNav(window.innerWidth < 1024);

    syncViewport();
    window.addEventListener('resize', syncViewport);
    return () => window.removeEventListener('resize', syncViewport);
  }, []);

  // Monitor online/offline status
  React.useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <div 
      className="flex flex-col min-h-screen relative transition-colors duration-200"
      style={{
        backgroundColor: dynamicColors.background,
      }}
    >
      {/* Subtle background pattern */}
      <div className="absolute inset-0 bg-hero-pattern opacity-20 pointer-events-none" />
      
      {/* Header */}
      <MobileHeader 
        title={title}
        showBack={showBack}
        onBack={onBack}
        rightAction={rightAction}
      />
      
      {/* Offline indicator */}
      <OfflineIndicator isOnline={isOnline} />
      
      {/* Main content area - Updated for Material 3 navigation */}
      <main 
        className="flex-1 overflow-y-auto relative z-10"
        data-route-scroll-container="true"
        style={showBottomNav && showMobileNav ? { 
          paddingBottom: 'max(112px, env(safe-area-inset-bottom))'
        } : {}}
      >
        {children || <Outlet />}
        <AppFooter />
      </main>
      
      {/* Bottom navigation - now fixed */}
      {showBottomNav && showMobileNav && (
        <Material3BottomNavigation />
      )}
    </div>
  );
};

export default MobileLayout;
